import { Fragment, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';

import { useEmptyStructure } from 'hooks/useObjectStructure';
import { DisplayModes } from 'services/webedi/types';
import { ColumnTypesInternal } from 'support/types/webediTable';
import { DataTable, useGenerateWebEDIColumns } from 'components/DataTable';
import { useDataTableContext } from 'components/DataTable/contexts/DataTableContext';
import { Button } from 'components/Form/Button';

import { useRemovePercentageColumn } from '../hooks/useRemovePercentageColumn';
import { WebEDISection } from '../WebEDISection/WebEDISection';

export const DocumentModificationGroup = ({
  config,
  dataPrefix,
  mode,
  multiLevel,
  isSubmitting,
}: {
  config: ui.GlobalModificationsSubSection;
  dataPrefix: string;
  mode: DisplayModes;
  multiLevel?: boolean;
  isSubmitting: boolean | undefined;
}) => {
  const { t } = useTranslation();
  const { fields, append, baseData, revisedData } = useDataTableContext();
  const configWithouPercentange = useRemovePercentageColumn(config.fields);
  const columns = useGenerateWebEDIColumns({
    config: configWithouPercentange,
    deletableRows: mode === DisplayModes.create || mode === DisplayModes.edit,
    displayMode: mode,
  });

  const ViewModeWrapper = useCallback(
    ({ children }: { children: ReactNode }) => (
      <WebEDISection header={{ title: '', description: config.label, spacing: 'small' }}>{children}</WebEDISection>
    ),
    [config.label],
  );
  const emptyModification = useEmptyStructure(fields);
  const Wrapper = mode === DisplayModes.view ? ViewModeWrapper : Fragment;
  return (
    <Wrapper>
      <DataTable
        meta={{ dataPrefix: dataPrefix, mode, isSubmitting }}
        columns={columns}
        data={fields}
        baseData={baseData as typeof fields}
        revisedData={revisedData as typeof fields}
        emptyState={
          multiLevel
            ? t(`webedi:${config.path}.empty_state.multi_level.${mode}.text`)
            : t(`webedi:${config.path}.empty_state.one_level.${mode}.text`)
        }
        footerMenu={
          (mode === DisplayModes.create || mode === DisplayModes.edit) && (
            <Button
              variant="secondary"
              size="extra-small"
              LeftIcon={PlusIcon}
              onClick={() =>
                append({ ...emptyModification, _internal: { [ColumnTypesInternal.procuros_added]: true } })
              }
              analyticsId="webedi:modification_group_modification_add"
              disabled={isSubmitting}
            >
              {t(`webedi:${config.path}.add_button.text`)}
            </Button>
          )
        }
        enableGlobalSearch={false}
        equalWidthColumns
      />
    </Wrapper>
  );
};

/**
 * i18n
 * t('webedi:allowances.empty_state.multi_level.view.text')
 * t('webedi:allowances.empty_state.multi_level.create.text')
 * t('webedi:allowances.empty_state.multi_level.edit.text')
 * t('webedi:charges.empty_state.multi_level.view.text')
 * t('webedi:charges.empty_state.multi_level.create.text')
 * t('webedi:charges.empty_state.multi_level.edit.text')
 * t('webedi:allowances.empty_state.one_level.view.text')
 * t('webedi:allowances.empty_state.one_level.create.text')
 * t('webedi:allowances.empty_state.one_level.edit.text')
 * t('webedi:charges.empty_state.one_level.view.text')
 * t('webedi:charges.empty_state.one_level.create.text')
 * t('webedi:charges.empty_state.one_level.edit.text')
 * t('webedi:allowances.add_button.text')
 * t('webedi:charges.add_button.text')
 */
